import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { reportingApi } from './api'
import type { CFMapping } from './types'

function useInvalidateReporting() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return () => {
    qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'reporting') })
  }
}

export function usePostingStatus() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reporting', 'posting-status'),
    queryFn: reportingApi.postingStatus,
  })
}

export function useUnpostedPreview(enabled: boolean) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reporting', 'unposted-preview'),
    queryFn: reportingApi.unpostedPreview,
    // Why: only fetch when a viewer opens the preview modal, so the page
    // doesn't pay a 4-table scan on every render.
    enabled,
  })
}

export function usePostTransactions() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: (fiscalStartMonth: number) => reportingApi.postTransactions(fiscalStartMonth),
    onSuccess: invalidate,
  })
}

export function useCFMappings() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reporting', 'cf-mappings'),
    queryFn: reportingApi.cfMappings,
  })
}

export function usePutCFMappings() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: (mappings: CFMapping[]) => reportingApi.putCfMappings(mappings),
    onSuccess: invalidate,
  })
}

export function useDeleteCFMapping() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: (accountCode: string) => reportingApi.deleteCfMapping(accountCode),
    onSuccess: invalidate,
  })
}

export function usePeriods(year: number) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reporting', 'periods', year),
    queryFn: () => reportingApi.periods(year),
  })
}

export function useGeneratePeriods() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: (args: { year: number; fiscalStartMonth: number }) =>
      reportingApi.generatePeriods(args.year, args.fiscalStartMonth),
    onSuccess: invalidate,
  })
}

export function useClosePeriod() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: (id: number) => reportingApi.closePeriod(id),
    onSuccess: invalidate,
  })
}

export function useReopenPeriod() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: (id: number) => reportingApi.reopenPeriod(id),
    onSuccess: invalidate,
  })
}

export function useAccountMappings() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reporting', 'account-mappings'),
    queryFn: reportingApi.accountMappings,
  })
}

export function useUpdateAccountMapping() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: (args: {
      id: number
      mapped_account_code: string
      mapping_status: string
    }) =>
      reportingApi.updateAccountMapping(args.id, {
        mapped_account_code: args.mapped_account_code,
        mapping_status: args.mapping_status,
      }),
    onSuccess: invalidate,
  })
}

export function useSnapshots() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reporting', 'snapshots'),
    queryFn: reportingApi.snapshots,
  })
}

export function useSnapshot(id: number | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reporting', 'snapshot', id ?? 'none'),
    queryFn: () => reportingApi.snapshot(id as number),
    enabled: id !== null,
  })
}

export function useSaveSnapshot() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: reportingApi.saveSnapshot,
    onSuccess: invalidate,
  })
}

export function useApproveSnapshot() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: (id: number) => reportingApi.approveSnapshot(id),
    onSuccess: invalidate,
  })
}

export function useGenerateMonthlySnapshots() {
  const invalidate = useInvalidateReporting()
  return useMutation({
    mutationFn: reportingApi.generateMonthlySnapshots,
    onSuccess: invalidate,
  })
}
